import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Sizes every masked media to its mask, because the mask no longer sizes it.
 *
 * Until now a media with a stencil mask fitted its source into the mask's bounds and ignored its
 * own `maxWidth`/`maxHeight`. A mask now only clips: every media fits its source into its own
 * `maxWidth`/`maxHeight` (times `scaleMultiplier`), so resizing a mask reveals or hides the media
 * instead of re-fitting it. Each masked media is given its mask's bounds as its `maxWidth` and
 * `maxHeight`, so it is drawn exactly as it was.
 */

type Size = { width: number; height: number };

const REFERENCE_RESOLUTIONS: Record<string, Size> = {
    landscape: { width: 1920, height: 1080 },
    square: { width: 1080, height: 1080 },
    vertical: { width: 1080, height: 1920 },
    fourfive: { width: 1080, height: 1350 },
};

const MEDIA_CLASS_TYPES = ['MediaAsset', 'TimeAwareMediaAsset'];
const DEFAULT_POLYGON_POINTS = [0.9, 0.1, 0.9, 0.9, 0.1, 0.9, 0.1, 0.1];

function sizeFromPoints(points: number[], resolution: Size): Size {
    const xs = points.filter((_value, index) => index % 2 === 0);
    const ys = points.filter((_value, index) => index % 2 === 1);
    return {
        width: (Math.max(...xs) - Math.min(...xs)) * resolution.width,
        height: (Math.max(...ys) - Math.min(...ys)) * resolution.height,
    };
}

/**
 * The size the renderer read off a stencil mask's bounds. Polygon bounds are combined the way the
 * renderer combines them — the larger of each extent of the start and end shapes.
 */
function maskSize(mask: Immutable.Map<string, unknown>, resolution: Size): Size {
    const classType = mask.get('classType');
    if (classType === 'PolygonMask') {
        const points = (key: string) =>
            (mask.get(key) as Immutable.List<number> | undefined)?.toArray() ??
            DEFAULT_POLYGON_POINTS;
        const start = sizeFromPoints(points('startPoints'), resolution);
        const end = sizeFromPoints(points('endPoints'), resolution);
        return {
            width: Math.max(start.width, end.width),
            height: Math.max(start.height, end.height),
        };
    }
    if (classType === 'CircleMask') {
        const radius = Math.max(
            (mask.get('startRadius') as number | undefined) ?? 0,
            (mask.get('endRadius') as number | undefined) ?? 0.5,
        );
        return { width: radius * 2, height: radius * 2 };
    }
    throw new Error(`Unknown mask type ${classType}`);
}

function migrateAssets(
    map: Immutable.Map<string, unknown>,
    listPath: Array<string | number>,
    resolution: () => Size,
) {
    const assets = map.getIn(listPath) as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    assets?.forEach((asset, index) => {
        const assetPath = [...listPath, index];
        if (asset.get('children')) {
            migrateAssets(map, [...assetPath, 'children'], resolution);
        }
        const mask = asset.get('mask') as Immutable.Map<string, unknown> | null | undefined;
        if (!MEDIA_CLASS_TYPES.includes(asset.get('classType') as string) || !mask) {
            return;
        }
        const { width, height } = maskSize(mask, resolution());
        map.setIn([...assetPath, 'maxWidth'], width);
        map.setIn([...assetPath, 'maxHeight'], height);
    });
}

export const migrationTo9_246 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((_scene: unknown, i: number) => {
                map.setIn(
                    ['slides', i],
                    this.migrateScene(map.getIn(['slides', i]) as Immutable.Map<string, unknown>),
                );
            });
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            const resolution = () => {
                const aspectRatio = map.get('aspectRatio') as string;
                const referenceResolution = REFERENCE_RESOLUTIONS[aspectRatio];
                if (!referenceResolution) {
                    throw new Error(`Unknown scene aspect ratio: ${aspectRatio}`);
                }
                return referenceResolution;
            };

            migrateAssets(map, ['children'], resolution);
            migrateAssets(map, ['stickers'], resolution);
            const subslides = map.get('subslides') as Immutable.List<unknown> | undefined;
            subslides?.forEach((_subslide, index) => {
                migrateAssets(map, ['subslides', index, 'children'], resolution);
            });
        });
    },
});
